import React, { useCallback, useMemo, useState } from 'react';
import { ButtonTW } from '../../../../tailwindComponents/buttonTW/ButtonTW';
import { Form, Formik, FormikProps } from 'formik';
import { DateField, FlexRow, Grid, SelectField, TimeField } from '../../../../ui-toolkit';
import { Show } from '../../../shared/Show';
import { SigningAppointmentDescriptionTooltip } from '../../signingAppointments/SigningAppointmentDescriptionTooltip';
import { SigningAppointmentAddressForm } from '../../signingAppointments/SigningAppointmentAddressForm';
import { timeZoneOptions } from '../commonOptions';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { BoldBodyTextTW } from '../../../../tailwindComponents/bodyText/BodyTextTW';
import { ISigningAppointmentEditFormData } from '../../../../types/signingAppointment/ISigningAppointmentEditFormData';
import { AppointmentEditFormValidationSchema } from './AppointmentEditFormValidationSchema';
import { eSigningMethod } from '../../../../types/signingAppointment/eSigningMethod';
import { useSelectCurrentOrder } from '../../../../selectors/order/selectCurrentOrder';
import {
  useListMethodsQuery,
  useListStatusesQuery,
  useListTypesQuery,
  useUpdateMutation,
} from '../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { getSigningTypeOptions } from '../../../../types/signingAppointment/ISigningTypeResponse';
import {
  getSigningMethodDescriptions,
  getSigningMethodOptions,
} from '../../../../types/signingAppointment/ISigningMethodResponse';
import { getStatusDescriptions } from '../../../../types/signingAppointment/ISigningAppointmentStatusResponse';
import { useSelectAppointmentFromPathParam } from '../../../../utilities/order/scheduling/useSomeAppointmentFromParam';
import { RateLanguageWitness } from './RateLanguageWitness';
import { ISigningAppointmentResponse } from 'types/signingAppointment/ISigningAppointmentResponse';
import { ToggleTW } from 'tailwindComponents/toggleTW/ToggleTW';
import { HybrydEventsTimeSelection } from '../CreateSigningAppointment/01_detailsStep/detailsForm/HybridEvents/HybridEventsTimeSelection';
import { extractDssValues, onChangeSameAsPropertyAddress } from './util';
import { checkForMutationSuccess } from '../../../../API/checkForMutationSuccess';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../types/shared/UUID';
import { CancelOrSubmit } from './CancelOrSubmit';
import { generateAppointmentOptions } from '../CreateSigningAppointment/01_detailsStep/detailsForm/utilities/generateTimeSlots';
import { computeStatusOptions } from 'types/signingAppointment/ComputeStatusOptions';
import { addDays, format } from 'date-fns';

export interface IAppointmentEditFormProps {
  onClose: () => void;
}

export const AppointmentEditForm: React.FC<IAppointmentEditFormProps> = ({ onClose }) => {
  const { orderId } = useParams<{ orderId: UUID }>();

  const { appointmentFromPath } = useSelectAppointmentFromPathParam();
  const [selectedSigningMethod, setSelectedSigningMethod] = useState<string>(appointmentFromPath?.signingMethod ?? '');
  const isEventAddressCreated = !!appointmentFromPath?.address?.id;
  const { data: order } = useSelectCurrentOrder();
  const { data: signingTypes = [] } = useListTypesQuery();
  const { data: signingMethods = [] } = useListMethodsQuery();
  const { data: signingStatuses = [] } = useListStatusesQuery();

  const signingTypeOptions = useMemo(() => getSigningTypeOptions(signingTypes), [signingTypes]);
  const methodDescriptions = useMemo(() => getSigningMethodDescriptions(signingMethods), [signingMethods]);
  const statusOptions = useMemo(
    () => computeStatusOptions(signingStatuses, appointmentFromPath),
    [appointmentFromPath, signingStatuses]
  );
  const statusDescriptions = useMemo(() => getStatusDescriptions(signingStatuses), [signingStatuses]);
  const signingMethodOptions = useMemo(() => getSigningMethodOptions(signingMethods), [signingMethods]);
  const isDssMagicLinkSent = appointmentFromPath?.isDigitalScheduling && !appointmentFromPath?.isDigitalSchedulingDraft;

  const getDssInitialValues = useMemo(() => {
    if (!!appointmentFromPath && appointmentFromPath.isDigitalScheduling) {
      return extractDssValues(appointmentFromPath);
    } else return {};
  }, [appointmentFromPath]);

  const [updateSigningAppointment] = useUpdateMutation();
  const onSaveEditions = useCallback(
    async (data: ISigningAppointmentEditFormData) => {
      const updateResponse = await updateSigningAppointment({
        id: data.id,
        orderId,
        data,
      });
      checkForMutationSuccess(updateResponse, 'updating signing appointment');
    },
    [updateSigningAppointment, orderId]
  );

  const onSaveEditForm = useCallback(
    async (formData: ISigningAppointmentEditFormData) => {
      const { isDigitalSchedulingDraft, isDigitalScheduling } = formData;

      // clearing address based on signer will provide location selection
      const signerWillProvideLocation = formData.signerWillProvideLocation;
      let address = !signerWillProvideLocation ? formData.address : null;

      if (!isDigitalSchedulingDraft && !isDigitalScheduling) {
        await onSaveEditions({
          ...formData,
          address,
          dssAppointmentOptions: undefined,
        });
        onClose();
        return;
      }

      if (isDigitalSchedulingDraft) {
        const {
          appointmentDate,
          appointmentTime,
          dssDate,
          dssDateRangeEnd,
          dssDateRangeStart,
          dssStartTime,
          dssEndTime,
          isDssDateRange,
          isDigitalSchedulingDraft,
          signerSelectsFromRange,
        } = formData;

        if (signerSelectsFromRange) {
          const dssAppointmentOptions = generateAppointmentOptions({
            signerSelectsFromRange,
            dssDate,
            dssDateRangeEnd,
            dssDateRangeStart,
            dssStartTime,
            dssEndTime,
            isDssDateRange,
          });
          await onSaveEditions({
            ...formData,
            address,
            appointmentDate: null,
            appointmentTime: null,
            dssAppointmentOptions,
            isDigitalSchedulingDraft,
          });
          onClose();
          return;
        }

        await onSaveEditions({
          ...formData,
          address,
          appointmentDate,
          appointmentTime,
          dssAppointmentOptions: null,
        });
        onClose();
        return;
      }

      await onSaveEditions({
        ...formData,
        dssAppointmentOptions: null,
      });
      onClose();
    },
    [onSaveEditions, onClose]
  );

  const tomorrow = useMemo(() => format(addDays(new Date(), 1), 'yyyy-MM-dd'), []);

  if (!appointmentFromPath) {
    return null;
  }

  return (
    <Formik
      initialValues={{
        ...appointmentFromPath,
        ...getDssInitialValues,
      }}
      validationSchema={AppointmentEditFormValidationSchema}
      onSubmit={onSaveEditForm}
      validateOnMount
    >
      {(formik: FormikProps<ISigningAppointmentResponse>) => (
        <Form>
          <div className={'flex flex-row pb-5'}>
            <CalendarIcon className={'mr-2 h-5 w-5'} />
            <BoldBodyTextTW>Edit Appointment Details</BoldBodyTextTW>
            <div className={'fa-pull-right ml-auto'}>
              <CancelOrSubmit onClose={onClose} />
            </div>
          </div>

          <div className={'space-y-4'}>
            <Grid columnCount={2} templateColumns={'.5fr .5fr'}>
              <SelectField
                data-test-id={`${appointmentFromPath.id}-scheduling-tab-status-select`}
                name={'status'}
                label="Status"
                placeholder={`Select a status`}
                options={statusOptions}
                formatOptionLabel={(option: any) => {
                  return (
                    <SigningAppointmentDescriptionTooltip
                      type={'status'}
                      option={option}
                      optionDescriptions={statusDescriptions}
                    />
                  );
                }}
              />
            </Grid>

            {/* Following section should only appear for non DSS or draft events.
              In therms of business logic, when the magic link has not been sent.
           */}
            <Show when={!isDssMagicLinkSent}>
              <div className={'flex flex-col'}>
                <Show when={!!formik.values.isDigitalSchedulingDraft}>
                  <ToggleTW name="signerSelectsFromRange" text="Signer specifies date and time" className="mb-3" />
                </Show>
                <Show when={!formik.values.signerSelectsFromRange} fallback={<HybrydEventsTimeSelection />}>
                  <Grid columnCount={3} templateColumns={'.3fr .3fr .15fr'}>
                    <DateField
                      name={`appointmentDate`}
                      label="Date"
                      isRequired
                      minDate={!!formik.values.isDigitalScheduling ? tomorrow : undefined}
                    />
                    <TimeField name={`appointmentTime`} label="Time" isRequired placeholder={'0:00'} />
                    <SelectField
                      name={'timeZone'}
                      label={'Zone '}
                      options={timeZoneOptions}
                      isDisabled={!isEventAddressCreated}
                    />
                  </Grid>
                </Show>
              </div>
              <Show when={selectedSigningMethod !== eSigningMethod.eSign}>
                <Grid>
                  <SigningAppointmentAddressForm
                    data-test-id={appointmentFromPath.id}
                    showLocationToggle={appointmentFromPath.isDigitalSchedulingDraft}
                    signerWillProvideLocation={formik.values.signerWillProvideLocation}
                    isLegacy={!formik.values.isDigitalScheduling}
                    isSignatureSync={formik.values.isSignatureSyncAppointment}
                  />
                  <Show when={!formik.values.signerWillProvideLocation}>
                    <FlexRow justifyContent={'start'}>
                      <ButtonTW secondary={true} onClick={() => onChangeSameAsPropertyAddress(formik, order)}>
                        {'Copy property address'}
                      </ButtonTW>
                    </FlexRow>
                  </Show>
                </Grid>
              </Show>
              <RateLanguageWitness
                appointmentFromPath={appointmentFromPath}
                selectedSigningMethod={selectedSigningMethod}
              />
              <Grid columnCount={2} templateColumns={'.5fr .5fr'}>
                <SelectField
                  menuPlacement="top"
                  isDisabled={true}
                  name={'signingMethod'}
                  options={signingMethodOptions}
                  label="Signing Method"
                  placeholder={`Select a method`}
                  onChange={(option: string) => setSelectedSigningMethod(option)}
                  formatOptionLabel={(option: any) => {
                    return (
                      <SigningAppointmentDescriptionTooltip
                        type={'status'}
                        option={option}
                        optionDescriptions={methodDescriptions}
                      />
                    );
                  }}
                />
                <SelectField
                  menuPlacement="top"
                  name={'signingType'}
                  options={signingTypeOptions}
                  label="Signing Type"
                  placeholder={`Select a category`}
                />
              </Grid>
            </Show>
          </div>
        </Form>
      )}
    </Formik>
  );
};
